import { DEFAULT_SPONSOR_ENGAGEMENT_PROGRAM } from "../../Utils/Constants/sponsorEngagementDefaults";

export type Poll2Dimension = {
  id: string;
  title: string;
  desc: string;
  lo: string;
  hi: string;
  evidence_type?: string;
  evidence_label?: string;
  evidence_placeholder?: string;
};

export type Poll2Draft = {
  involvement: number | null;
  relationship: number | null;
  knowledge: number | null;
  comments: string;
  evidence: Record<string, string>;
};

export type Poll2Tier = {
  min: number;
  max: number;
  label?: string;
};

const POLL2_EVIDENCE_META: Record<
  string,
  { evidence_label: string; evidence_placeholder: string }
> = {
  involvement: {
    evidence_label: "Why this rating? (optional)",
    evidence_placeholder:
      "One concrete example — meeting cadence, response time…",
  },
  relationship: {
    evidence_label: "Why this rating? (optional)",
    evidence_placeholder:
      "Customer relationship moment that justifies the score.",
  },
  knowledge: {
    evidence_label: "Why this rating? (optional)",
    evidence_placeholder: "Specific business signal they referenced correctly.",
  },
};

export const enrichPoll2Dimensions = (
  dimensions: Poll2Dimension[]
): Poll2Dimension[] =>
  dimensions.map(d => ({
    ...d,
    evidence_type: "text",
    ...POLL2_EVIDENCE_META[d.id],
  }));

export const emptyPoll2Draft = (): Poll2Draft => ({
  involvement: null,
  relationship: null,
  knowledge: null,
  comments: "",
  evidence: {},
});

export const poll2DraftFromAccount = (account: {
  poll2_dimensions?: Record<string, number> | null;
  poll2_evidence?: Record<string, string> | null;
}): Poll2Draft => {
  const dims = account.poll2_dimensions;
  if (!dims) return emptyPoll2Draft();
  return {
    involvement: dims.involvement ?? null,
    relationship: dims.relationship ?? null,
    knowledge: dims.knowledge ?? null,
    comments: "",
    evidence: { ...(account.poll2_evidence ?? {}) },
  };
};

export const poll2FilledCount = (draft: Poll2Draft): number =>
  [draft.involvement, draft.relationship, draft.knowledge].filter(
    v => v != null
  ).length;

export const poll2AllFilled = (draft: Poll2Draft): boolean =>
  poll2FilledCount(draft) === 3;

export const poll2RunningTotal = (draft: Poll2Draft): number =>
  (draft.involvement || 0) + (draft.relationship || 0) + (draft.knowledge || 0);

export const poll2Lookup = (
  raw: number,
  tiers: Poll2Tier[] = DEFAULT_SPONSOR_ENGAGEMENT_PROGRAM.scoring_config
    .poll2_tiers as Poll2Tier[]
): Poll2Tier | null => {
  for (const t of tiers) {
    if (raw >= t.min && raw <= t.max) return t;
  }
  return null;
};

export const buildSubmitEvidence = (
  draft: Poll2Draft,
  dimensionIds: string[]
): Record<string, string> => {
  const out: Record<string, string> = {};
  for (const id of dimensionIds) {
    const v = draft.evidence[id];
    if (v && v.trim()) out[id] = v.trim();
  }
  return out;
};

export const hasPoll2DraftProgress = (draft: Poll2Draft): boolean =>
  poll2FilledCount(draft) > 0 ||
  Boolean(draft.comments.trim()) ||
  Object.values(draft.evidence).some(v => v.trim());

/** Label for the rated sponsor, including primary/secondary when known. */
export const formatSponsorLabel = (
  name?: string | null,
  role?: string | null
): string | null => {
  if (!name) return null;
  if (role === "primary") return `Primary Sponsor: ${name}`;
  if (role === "secondary") return `Secondary Sponsor: ${name}`;
  return `Sponsor: ${name}`;
};
